
import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Building2,
  FolderOpen,
  Workflow,
  Zap,
  CheckCircle2,
  List,
  Maximize,
  Minimize,
  X,
  AlertTriangle,
  ArrowRightLeft,
  Plus,
  Clock,
  Settings,
} from "lucide-react";
import { cn } from '@flowstate/utils';
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { VERIFICATION_CAST, useVerificationStudio } from "@/features/verification-studio";
import { ActiveSidebar } from "../../types/chat-sidebar-types";
import { Conversation } from "../../hooks/useChat";
import { agentBranding } from "../../config/agentBranding";
import { logger } from "@/lib/logger";
import { NinjaStarFlyout } from "@/components/ui/ninja-star-flyout";
import { ConsoleToggle } from "@flowstate/console";

interface ChatHeaderProps {
  isFullscreen: boolean;
  onClose: () => void;
  onToggleFullscreen: () => void;
  activeSidebar: ActiveSidebar;
  onToggleSidebar: (sidebar: ActiveSidebar) => void;
  currentConversation: Conversation | null;
  onStartNewChat: () => void;
}

export function ChatHeader({
  isFullscreen,
  onClose,
  onToggleFullscreen,
  activeSidebar,
  onToggleSidebar,
  currentConversation,
  onStartNewChat
}: ChatHeaderProps) {
  const { currentOrganization, switchOrganization } = useCurrentOrganization();
  const { currentProject } = useCurrentProject();
  const { enabled: studio } = useVerificationStudio();
  const organizationLabel = studio
    ? VERIFICATION_CAST.organization.name
    : currentOrganization?.name;
  const projectLabel = studio ? VERIFICATION_CAST.project.name : currentProject?.name;

  // Phase 5 Guard Rail: Check for cross-org session
  const isCrossOrgSession = currentConversation?.organization_id && 
    currentOrganization?.id && 
    currentConversation.organization_id !== currentOrganization.id;

  // Handle switching to conversation's organization
  const handleSwitchOrg = async (orgId: string) => {
    try {
      await switchOrganization(orgId);
    } catch (error) {
      logger.error('Failed to switch organization:', { error: error });
    }
  };

  const ninjaItems: { key: ActiveSidebar; icon: React.ReactNode; label: string }[] = [
    { key: 'workflows', icon: <Workflow className="h-4 w-4" />, label: 'Workflows' },
    { key: 'quick-actions', icon: <Zap className="h-4 w-4" />, label: 'Quick Actions' },
    { key: 'action-items', icon: <CheckCircle2 className="h-4 w-4" />, label: 'Action Items' },
    { key: 'background-jobs', icon: <Clock className="h-4 w-4" />, label: 'Background Jobs' },
    { key: 'chat', icon: <List className="h-4 w-4" />, label: 'Conversations' },
    { key: 'settings', icon: <Settings className="h-4 w-4" />, label: 'Agent Settings' },
  ];

  return (
    <div className="flex flex-shrink-0 flex-col border-b border-border/20 z-20 bg-background/60 backdrop-blur-xl rounded-t-2xl" data-testid="cortex-header">
       {/* Cross-Org Warning Banner */}
       {isCrossOrgSession && (
        <div className="bg-amber-100/80 dark:bg-amber-900/20 px-3 py-2 flex flex-col gap-2 border-b border-amber-200/50 dark:border-amber-800/50 backdrop-blur-sm rounded-t-2xl">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800 dark:text-amber-200">
              <span className="font-semibold">Context Mismatch:</span> This conversation belongs to a different organization.
            </div>
          </div>
          <div className="flex gap-2 pl-6">
            <Button 
              size="sm" 
              variant="outline" 
              className="h-6 text-[10px] bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 border-amber-200 dark:border-amber-800 rounded-lg"
              onClick={() => handleSwitchOrg(currentConversation.organization_id!)}
            >
              <ArrowRightLeft className="h-3 w-3 mr-1" /> Switch Org
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              className="h-6 text-[10px] bg-white/50 dark:bg-black/20 hover:bg-white dark:hover:bg-black/40 border-amber-200 dark:border-amber-800 rounded-lg"
              onClick={onStartNewChat}
            >
              <Plus className="h-3 w-3 mr-1" /> Start New
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center px-3 py-2">
        <div className="flex items-center gap-2.5">
          <Avatar className="h-8 w-8 ring-2 ring-primary/20">
            <AvatarImage src={agentBranding.avatarSrc} alt={agentBranding.name} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {agentBranding.avatarFallback}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-semibold text-sm">{agentBranding.name}</h3>
            {(organizationLabel || projectLabel) && (
              <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                {organizationLabel && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[9px] font-medium bg-blue-100/80 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded-md">
                    <Building2 className="h-2.5 w-2.5" />
                    <span className="truncate max-w-[80px]">{organizationLabel}</span>
                  </span>
                )}
                {projectLabel && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0 text-[9px] font-medium bg-green-100/80 text-green-700 dark:bg-green-900/20 dark:text-green-300 rounded-md">
                    <FolderOpen className="h-2.5 w-2.5" />
                    <span className="truncate max-w-[80px]">{projectLabel}</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <NinjaStarFlyout
            variant="chat"
            itemCount={ninjaItems.length}
            closeOnItemClick
            data-testid="cortex-panels-flyout"
            aria-label="Toggle panels"
            trigger={
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="lightning-trigger chat-header-icon-btn h-8 w-8 min-h-8 min-w-8 shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary shadow-sm hover:bg-primary/25 hover:border-primary/50"
                      aria-label="Toggle panels"
                    >
                      <Zap className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Panels</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            }
          >
            {ninjaItems.map((item, i) => (
              <div
                key={item.key}
                className="ninja-item"
                style={{ "--i": i + 1 } as React.CSSProperties}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onToggleSidebar(item.key)}
                        className={cn(
                          "flex items-center justify-center",
                          activeSidebar === item.key && "ring-2 ring-primary ring-offset-1",
                        )}
                        aria-label={item.label}
                        aria-pressed={activeSidebar === item.key}
                      >
                        {item.icon}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>{item.label}</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ))}
          </NinjaStarFlyout>

          <ConsoleToggle
            variant="ghost"
            size="icon"
            className="h-8 w-8 min-h-8 min-w-8 shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary shadow-sm hover:bg-primary/25 hover:border-primary/50"
          />

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleFullscreen}
                  aria-label={isFullscreen ? "Minimize chat" : "Maximize chat"}
                  className="chat-header-icon-btn hidden sm:inline-flex h-8 w-8 min-h-8 min-w-8 shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary shadow-sm hover:bg-primary/25 hover:border-primary/50"
                  data-testid="cortex-fullscreen-btn"
                >
                  {isFullscreen ? (
                    <Minimize className="h-4 w-4" />
                  ) : (
                    <Maximize className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{isFullscreen ? "Minimize" : "Fullscreen"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label="Close chat"
                  className="chat-header-icon-btn h-8 w-8 min-h-8 min-w-8 shrink-0 rounded-full bg-primary/15 border border-primary/40 text-primary shadow-sm hover:bg-primary/25 hover:border-primary/50"
                  data-testid="cortex-close-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Close</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
